/**
 * A 402, rendered as an offer rather than an error.
 *
 * "Not bought" (product_required), "bought but spent" (credits_exhausted) and
 * "bought but this plan stops here" (plan_limit) are all things a payment
 * fixes, and the fix lives in one place: the billing page in the house. So
 * every one of them is a short sentence and the same link, never a red banner
 * — an error tells you something broke; this tells you what it costs.
 */
import { paywallCopy } from '../../billing/credits';
import { PRODUCT_INFO, shellUrl } from '../../shell/products';
import type { Paywall, Product } from '../../types';

function productName(product: Product): string {
  return PRODUCT_INFO[product]?.name ?? product;
}

export function billingHref(): string {
  return `${shellUrl('app', window.location.hostname, window.location.origin)}/billing`;
}

export default function PaywallNotice({
  paywall,
  onDismiss,
  compact = false,
}: {
  paywall: Paywall;
  onDismiss?: () => void;
  /** One line for a control bar; otherwise a card. */
  compact?: boolean;
}) {
  const copy = paywallCopy(paywall, productName);
  const cta = paywall.kind === 'credits' ? 'Credits & plans' : 'See plans';
  // A retired product is not for sale: no link to buy it.
  const sellable = paywall.kind !== 'retired';
  if (compact) {
    return (
      <span className="text-[11px] text-amber-700 dark:text-amber-300" data-testid="paywall">
        {copy}
        {sellable && (
          <>
            {' '}
            <a href={billingHref()} className="underline hover:no-underline">
              {cta}
            </a>
          </>
        )}
      </span>
    );
  }
  return (
    <div
      className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 flex flex-wrap items-center justify-between gap-3"
      data-testid="paywall"
    >
      <p className="text-sm text-amber-900 dark:text-amber-100">{copy}</p>
      <div className="flex items-center gap-3">
        {sellable && (
          <a
            href={billingHref()}
            className="px-3 py-1.5 text-sm rounded bg-amber-600 text-white hover:bg-amber-700"
          >
            {cta}
          </a>
        )}
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="text-sm text-amber-800 dark:text-amber-200 hover:underline"
          >
            Dismiss
          </button>
        )}
      </div>
    </div>
  );
}
